import React, { useMemo, useState } from 'react';
import { isEmpty } from 'lodash';

import { SortableTable, TooltipIcon } from '~/components/core';
import EmptyState from '~/components/core/EmptyState';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import InfoIcon from '~/components/icons/InfoIcon';
import PencilIcon from '~/components/icons/PencilIcon';
import ShieldIcon from '~/components/icons/ShieldIcon';
import InlineIconButton from '~/components/InlineIconButton';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import Collapse from '~/components/ui/atoms/Collapse';
import useDataFetcher from '~/components/useDataFetcher';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';
import type { GuardrailRuleInfo } from '~/server_shared/openapi/data-contracts';
import colors from '~/theme/tailwind/colors';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled, isLegacyOrgFeatureEnabled } from '~/Utils';
import cn from '~/Utils/cn';

import AgentConfigChip, { AGENT_CHIP_TONE } from '../AgentConfigChip';
import copies from '../copies';

import { isClaimActionGuardrail } from './claimActionGuardrails';
import EditClaimActionDialog from './EditClaimActionDialog';
import { executionModeToAutomaticExecutionChip } from './executionMode';
import type { EffectiveClaimActionConfig, EffectiveClaimActionConfigList, ScopeRow } from './types';

interface ActionsCardProps {
  organizationId: number;
  scope: ScopeRow;
  /** The conductor's switch for this scope, which dims this card without disabling it. */
  isConductorEnabled: boolean;
}

// SortableTable keys rows by `row.id`; the action key is unique per scope, so it is the identity.
// No derived display fields: every column that shows a chip reads the action's own values, so
// there is nothing here that could drift from what the dialog saves.
interface ActionTableRow extends EffectiveClaimActionConfig {
  id: string;
}

interface UseGuardrailCountsArgs {
  organizationId: number;
  organization: OrganizationModel;
  scope: ScopeRow;
  actions: EffectiveClaimActionConfig[] | undefined;
  shouldFetch: boolean;
}

/**
 * The guardrail read behind the Guardrails column, counted per action.
 *
 * The same gate `ClaimActionGuardrailsSection` applies. A hospitality-only organization has no
 * guardrails, so that section renders nothing and fetches nothing there, and a count here would be
 * a number about a feature the organization does not have — the column is dropped entirely rather
 * than shown as zero.
 *
 * One read for the whole card. The endpoint answers with every rule the organization has and
 * takes no scope filter, so reading it per row would multiply an unbounded response by the number
 * of actions. It is also the read the edit dialog's guardrails section already makes, counted
 * with that section's own predicate, so the column and the list inside the dialog cannot disagree
 * about what counts as this action's guardrail.
 */
const useGuardrailCounts = ({ organizationId, organization, scope, actions, shouldFetch }: UseGuardrailCountsArgs) => {
  const isGuardrailsEngineEnabled = !isLegacyOrgFeatureEnabled(
    organization,
    LEGACY_ORG_FEATURES.HOSPITALITY_NO_GUARDRAILS
  );

  const { data: guardrailRules, reloadData } = useDataFetcher<GuardrailRuleInfo[]>(
    `/api/v1/organizations/${organizationId}/guardrails/rules`,
    null,
    { shouldFetch: shouldFetch && isGuardrailsEngineEnabled, shouldOpenErrorDialog: false }
  );

  // `null` for an action whose event cannot trigger a guardrail at all: the dialog offers it no
  // guardrails, so `0` would read as "none configured yet" and invite an operator to add one that
  // could never fire. No entry at all until the rules have arrived — while the read is in flight or
  // after it failed (silently, see ActionsCard) there is no count to show, and `0` would be a false one.
  const guardrailCountByActionKey = useMemo(() => {
    if (!guardrailRules) {
      return new Map<string, number | null>();
    }
    return new Map<string, number | null>(
      (actions ?? []).map((action) => [
        action.key,
        !isEmpty(action.guardrail_event_keys)
          ? guardrailRules.filter((rule) =>
              isClaimActionGuardrail(rule, action.guardrail_event_keys, scope, action.guardrails_required_conditions)
            ).length
          : null,
      ])
    );
  }, [guardrailRules, actions, scope]);

  const reloadGuardrailCounts = () => {
    if (isGuardrailsEngineEnabled) {
      reloadData();
    }
  };

  return { isGuardrailsEngineEnabled, guardrailCountByActionKey, reloadGuardrailCounts };
};

/**
 * The Guardrails column header. A node rather than a bare string: the column reads in three states
 * and only one of them is a number, so the header carries the key to them. An explicit icon rather
 * than the column's own `toolTip`, which tips the whole header cell with nothing on screen to say
 * it is there.
 */
const GuardrailsColumnLabel: React.FC = () => (
  <span className="flex items-center gap-4">
    {copies.actions.guardrailsColumn}
    <TooltipIcon title={copies.actions.guardrailsColumnTooltip} placement="top" arrow>
      <InfoIcon size={14} iconColor={colors.slate[700]} />
    </TooltipIcon>
  </span>
);

/**
 * One action's guardrail count. Three states, not two. The design mock draws an em-dash for a zero
 * as well as for an action that can carry no guardrail at all, but those say different things to
 * an operator: one is "none authored yet", which they can act on, and the other is "never possible
 * here", which they cannot. Only the second gets the dash — and so does a count not yet known
 * (`undefined`), which claims neither.
 */
const GuardrailCountCell: React.FC<{ count: number | null | undefined }> = ({ count }) => {
  if (count === null || count === undefined) {
    return (
      <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {copies.actions.guardrailsNotApplicable}
      </Text>
    );
  }
  if (count === 0) {
    return (
      <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {copies.actions.guardrailsNone}
      </Text>
    );
  }
  return (
    <AgentConfigChip
      label={String(count)}
      tone={AGENT_CHIP_TONE.ATTENTION}
      icon={<ShieldIcon size={12} iconColor={colors.orange[700]} />}
    />
  );
};

interface ActionsCardHeaderProps {
  isOpen: boolean;
  onToggle: () => void;
  /** `null` until the list has actually loaded, so the card never claims "0 actions" while reading. */
  actionCount: number | null;
}

/**
 * The card's collapse toggle. A real button, like the section header above it: keyboard-reachable,
 * and it announces its own state rather than leaving the chevron to imply it. The count sits here
 * so it is readable collapsed.
 */
const ActionsCardHeader: React.FC<ActionsCardHeaderProps> = ({ isOpen, onToggle, actionCount }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={isOpen}
    className="flex cursor-pointer items-center gap-8 border-0 bg-transparent p-0 text-left"
  >
    <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="flex-1">
      {copies.actions.title}
    </Text>
    {actionCount === null ? null : (
      <AgentConfigChip label={copies.actions.count(actionCount)} tone={AGENT_CHIP_TONE.NEUTRAL} />
    )}
  </button>
);

/**
 * What the agent may do on a claim in this scope.
 *
 * Owns its own read and its own reload rather than taking the list from the section above: each
 * action is written by its own PUT, so the list has to be re-read after a save and there is no
 * aggregate payload for the editor to hold.
 *
 * The card is dimmed while the conductor is off and stays fully interactive, because the
 * conductor's switch does not gate these actions — they are listed and executed on their own
 * ai_integration endpoints. Disabling the card would also stop an operator setting the actions up
 * before turning the conductor on, which is the order the work is normally done in.
 */
const ActionsCard: React.FC<ActionsCardProps> = ({ organizationId, scope, isConductorEnabled }) => {
  // The target organization, not the signed-in admin's: this renders inside the sysconfig subtree.
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  // The tab this card sits in is gated on agent_configuration, so without this gate an
  // organization that never opted into claim actions would get the card anyway.
  const isClaimActionsEnabled = isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CLAIM_ACTIONS);

  const baseUrl = `/sysconfig/api/v1/organizations/${organizationId}/claim_actions`;
  // The scope key travels as query params. `sub_organization_id` is omitted entirely for a flat
  // organization rather than sent as a literal null, which the query schema rejects; the backend
  // resolves the absent param to the org-level configuration (`sub_organization_id IS NULL`).
  const queryString =
    scope.sub_organization_id === null
      ? `lob=${encodeURIComponent(scope.lob)}`
      : `lob=${encodeURIComponent(scope.lob)}&sub_organization_id=${scope.sub_organization_id}`;

  // A secondary read on a screen the operator opened for the conductor, so the failure is reported
  // without a modal: the SkeletonTable below already shows it, and the card is mounted inside the
  // section's `isOpen` body, so a dialog would return on every collapse and re-expand.
  const { data, isLoading, isError, reloadData } = useDataFetcher<EffectiveClaimActionConfigList>(
    `${baseUrl}?${queryString}`,
    null,
    { shouldFetch: isClaimActionsEnabled, shouldOpenErrorDialog: false }
  );
  const [editAction, setEditAction] = useState<EffectiveClaimActionConfig | null>(null);
  // Open by default: the actions are the point of the card, and an operator who expanded the
  // section above did so to reach them.
  const [isOpen, setIsOpen] = useState(true);

  const { isGuardrailsEngineEnabled, guardrailCountByActionKey, reloadGuardrailCounts } = useGuardrailCounts({
    organizationId,
    organization,
    scope,
    actions: data?.configs,
    shouldFetch: isClaimActionsEnabled,
  });

  // The dialog's guardrails section writes each rule the moment it is saved or deleted and reloads
  // only its own read, so the count here is re-read on every close — Save included, which closes too.
  const handleDialogClose = () => {
    setEditAction(null);
    reloadGuardrailCounts();
  };

  if (!isClaimActionsEnabled) {
    return null;
  }

  const actions: EffectiveClaimActionConfig[] = data?.configs ?? [];
  const tableRows: ActionTableRow[] = actions.map((action) => ({
    ...action,
    id: action.key,
  }));

  // The table is read-only — a row opens the edit dialog, and nothing is edited in place. The
  // action key is deliberately not a column: the editor above already names the scope, and the
  // display name identifies the row.
  const columns = [
    { id: 'display_name', label: copies.actions.displayNameColumn, numeric: false },
    {
      id: 'instructions',
      label: copies.actions.instructionsColumn,
      numeric: false,
      specialCell: (row: ActionTableRow) => (row.instructions ? row.instructions : null),
    },
    {
      id: 'enabled',
      label: copies.actions.enabledColumn,
      disableSort: true,
      // Its own column rather than being left implicit in Automatic Execution's "Disabled": that
      // one answers what the agent may do unattended, this one whether the action exists for the
      // agent at all, and an operator scanning the table is usually asking the second.
      specialCell: (row: ActionTableRow) => (
        <AgentConfigChip
          label={row.enabled ? copies.actions.enabledYes : copies.actions.enabledNo}
          tone={row.enabled ? AGENT_CHIP_TONE.ON : AGENT_CHIP_TONE.NEUTRAL}
        />
      ),
    },
    {
      id: 'automatic_execution',
      label: copies.actions.automaticExecution,
      disableSort: true,
      specialCell: (row: ActionTableRow) => {
        const { label, tone } = executionModeToAutomaticExecutionChip(row);
        return <AgentConfigChip label={label} tone={tone} />;
      },
    },
    ...(isGuardrailsEngineEnabled
      ? [
          {
            id: 'guardrails',
            label: <GuardrailsColumnLabel />,
            disableSort: true,
            specialCell: (row: ActionTableRow) => <GuardrailCountCell count={guardrailCountByActionKey.get(row.id)} />,
          },
        ]
      : []),
    {
      id: 'actions',
      label: copies.actions.editColumn,
      disableSort: true,
      specialCell: (row: ActionTableRow) => (
        <InlineIconButton icon={PencilIcon} tooltipTitle={copies.editor.edit} onClick={() => setEditAction(row)} />
      ),
    },
  ];

  return (
    <>
      <div
        className={cn(
          'flex flex-col gap-12 rounded-md border border-solid border-slate-500 p-16',
          // Visual only: see the component docstring. The card stays interactive.
          !isConductorEnabled && 'opacity-60'
        )}
      >
        <ActionsCardHeader
          isOpen={isOpen}
          onToggle={() => setIsOpen((wasOpen) => !wasOpen)}
          actionCount={isLoading || isError ? null : actions.length}
        />
        {/* Hidden, not unmounted — no `unmountOnExit`. The read above lives in this component, so
            unmounting would re-issue it on every expand, and would discard the edit dialog with it.
            The enclosing section collapses by unmounting; this one deliberately does not. */}
        <Collapse in={isOpen}>
          <div className="flex flex-col gap-12">
            <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {copies.actions.helper}
            </Text>
            {isConductorEnabled ? null : (
              <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                {copies.actions.conductorOffNote}
              </Text>
            )}
            {isLoading || isError ? (
              <SkeletonTable rowsCount={3} columnsCount={columns.length} isError={isError} />
            ) : (
              <SortableTable
                columns={columns}
                rows={tableRows}
                // SortableTable hands the handler the row's *id*, not the row and not an event
                // (`SortableTableCell.handleCellClick` calls `onRowClick(row.id)`), and the id here
                // is the action key. The pencil needs no `stopPropagation`: it sets the same state
                // this does, so the two firing together is idempotent — the sibling Experts table
                // relies on exactly that.
                onRowClick={(actionKey: string) => setEditAction(tableRows.find((row) => row.id === actionKey) ?? null)}
                emptyStateComponent={
                  <EmptyState title={copies.actions.emptyTitle} subtitle={copies.actions.emptySubtitle} />
                }
              />
            )}
          </div>
        </Collapse>
      </div>
      {editAction ? (
        <EditClaimActionDialog
          action={editAction}
          scope={scope}
          baseUrl={baseUrl}
          onClose={handleDialogClose}
          onSaved={() => reloadData()}
        />
      ) : null}
    </>
  );
};

export default ActionsCard;
